import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import { useSnackbar } from "notistack";
import PasswordLessAuthService from "../../core/service/passwordless-service";

// Passkey enrollment + management for admins. Rendered on the post-login landing so an admin is prompted to
// enroll right after their (freshest) sign-in, and can list/remove existing passkeys. Requires a live Cognito
// session — Amplify's WebAuthn APIs authorise with the current access token, enforced server-side by Cognito,
// so a passkey can only be bound to an already-authenticated user.
export default function PasskeyManager() {
  const passwordLessAuthService = new PasswordLessAuthService();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [credentials, setCredentials] = useState<any[]>([]);
  // Browsers without WebAuthn can't use passkeys — hide the surface entirely rather than show a dead button.
  const supported = typeof window !== "undefined" && !!(window as any).PublicKeyCredential;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result: any = await passwordLessAuthService.listPasskeys();
      setCredentials(result?.credentials ?? []);
    } catch (error) {
      setCredentials([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (supported) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [supported, refresh]);

  const register = async () => {
    setBusy(true);
    try {
      await passwordLessAuthService.registerPasskey();
      enqueueSnackbar("Passkey registered — you can use it to sign in next time.", { variant: "success" });
      await refresh();
    } catch (error) {
      enqueueSnackbar("Could not register a passkey. Please try again.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (credentialId: string) => {
    setBusy(true);
    try {
      await passwordLessAuthService.deletePasskey(credentialId);
      await refresh();
    } catch (error) {
      enqueueSnackbar("Could not remove the passkey.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return null;
  }

  return (
    <Card sx={{ width: "100%", p: 2.5, mt: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <KeyRoundedIcon color="primary" />
        <Typography variant="h6">Passkeys</Typography>
      </Stack>

      {loading ? (
        <CircularProgress size={22} />
      ) : credentials.length === 0 ? (
        <Stack spacing={1.5}>
          <Alert severity="info">Set up a passkey to sign in faster next time — no email or SMS codes.</Alert>
          <Button
            variant="contained"
            onClick={register}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <KeyRoundedIcon />}
          >
            Set up a passkey
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {credentials.map((credential: any) => (
            <Stack
              key={credential.credentialId}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 0.75, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}
            >
              <Typography variant="body2">
                {credential.friendlyCredentialName || credential.relyingPartyId || credential.credentialId}
              </Typography>
              <IconButton size="small" onClick={() => remove(credential.credentialId)} disabled={busy} aria-label="Remove passkey">
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button variant="text" onClick={register} disabled={busy} sx={{ alignSelf: "flex-start" }}>
            Add another passkey
          </Button>
        </Stack>
      )}
    </Card>
  );
}
